import React from 'react';
import { FormControl, FormLabel, FormGroup, Grid, TextField, FormHelperText } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useFormikContext } from 'formik';
import { useFormikHelper } from '@/hooks/useFormikHelper';
import { IndustrySelect } from '@/components/ClientDetailEntry/industrySelect';
import { AnzsicCodeSearch } from '@/components/ClientDetailEntry/anzsicCodeSearch';
import { SourceOfBusinessSelect } from '@/components/ClientDetailEntry/sourceOfBusinessSelect';
import FolioCheckbox from '@/components/FolioCheckbox';
import {
	ANNUAL_TURNOVER,
	ANZSIC_CODE,
	CLIENT_CATEGORY,
	CLIENT_CATEGORY_LABELS,
	NO_EMPLOYEES,
	PROSPECT_AMOUNT,
	SOURCE_OF_BUSINESS,
	WEBSITE,
} from '../validationSchema';

export const NewClientFields = ({ loading, country }) => {
	const { values, setFieldValue, getFieldProps } = useFormikContext();
	const { getError, getErrorMessage } = useFormikHelper();

	return (
		<>
			<Grid size={12}>
				<FormLabel>{'Client Category'}</FormLabel>
				<FormGroup row sx={{ width: '100%' }}>
					<Grid container width={'100%'} columnSpacing={2} rowSpacing={1}>
						{Object.entries(CLIENT_CATEGORY_LABELS).map((entry) => {
							const [value, label] = entry;
							return (
								<Grid size={{ xs: 6 }} key={`client-category-${value}`}>
									<FolioCheckbox
										checked={values[CLIENT_CATEGORY].includes(value)}
										label={label}
										onChange={() => {
											let newValues;
											if (values[CLIENT_CATEGORY].includes(value)) {
												newValues = values[CLIENT_CATEGORY].filter(
													(i) => i !== value,
												);
											} else {
												newValues = [...values[CLIENT_CATEGORY], value];
											}
											setFieldValue(CLIENT_CATEGORY, newValues);
										}}
										disabled={loading}
									/>
								</Grid>
							);
						})}
					</Grid>
				</FormGroup>
			</Grid>
			<Grid
				size={{
					xs: 12,
					md: 4,
				}}
			>
				<FormLabel>{'Website Address'}</FormLabel>
				<TextField
					fullWidth
					name={WEBSITE}
					{...getFieldProps(WEBSITE)}
					error={getError(WEBSITE)}
					helperText={getErrorMessage(WEBSITE)}
					disabled={loading}
				/>
				<FormHelperText>
					Please provide a web address if you have a website or social media.
				</FormHelperText>
			</Grid>
			<Grid
				size={{
					xs: 12,
					md: 4,
				}}
			>
				<FormLabel>{'Industry'}</FormLabel>
				<IndustrySelect
					initialValue={values.industry}
					disabled={loading}
					onChange={(value) => setFieldValue('industry', value)}
				/>
			</Grid>
			<Grid size={{ xs: 12, md: 4 }}>
				<FormLabel required={country === 'AU'}>{'Standard Industry Code'}</FormLabel>
				<AnzsicCodeSearch
					initialValue={values[ANZSIC_CODE]}
					onSelect={(value) => setFieldValue('anzsicCode', value)}
					disabled={loading}
				/>
				{country === 'AU' && (
					<FormHelperText error={getError(ANZSIC_CODE)}>
						{getErrorMessage(ANZSIC_CODE)}
					</FormHelperText>
				)}
			</Grid>
			<Grid size={{ xs: 12, md: 6 }}>
				<FormControl fullWidth>
					<FormLabel>{'No. Of Employees'}</FormLabel>
					<NumericFormat
						customInput={TextField}
						decimalScale={0}
						fixedDecimalScale
						thousandSeparator
						value={values.numberofemployees}
						onValueChange={({ value }) => {
							setFieldValue(NO_EMPLOYEES, value);
						}}
						disabled={loading}
						name={NO_EMPLOYEES}
					/>
				</FormControl>
			</Grid>
			<Grid size={{ xs: 12, md: 6 }}>
				<FormControl fullWidth>
					<FormLabel>{'Annual Turnover'}</FormLabel>
					<NumericFormat
						prefix='$'
						decimalScale={0}
						fixedDecimalScale
						thousandSeparator
						allowNegative={false}
						customInput={TextField}
						value={values.annualrevenue}
						onValueChange={({ floatValue }) => {
							setFieldValue(ANNUAL_TURNOVER, floatValue);
						}}
						disabled={loading}
						name={ANNUAL_TURNOVER}
					/>
				</FormControl>
			</Grid>
			{values.isProspect && (
				<Grid size={{ xs: 12, md: 6 }}>
					<FormControl fullWidth>
						<FormLabel>Prospect Amount</FormLabel>
						<NumericFormat
							prefix='$'
							decimalScale={0}
							fixedDecimalScale
							thousandSeparator
							allowNegative={false}
							customInput={TextField}
							value={values[PROSPECT_AMOUNT]}
							onValueChange={({ floatValue }) => {
								setFieldValue(PROSPECT_AMOUNT, floatValue);
							}}
							disabled={loading}
							name={PROSPECT_AMOUNT}
						/>
					</FormControl>
				</Grid>
			)}
			<Grid
				size={{
					xs: 12,
					md: 6,
				}}
			>
				<FormLabel>{'Source Of Business'}</FormLabel>
				<SourceOfBusinessSelect
					initialValue={values.sourceOfBusiness}
					disabled={loading}
					onChange={(value) => setFieldValue(SOURCE_OF_BUSINESS, value)}
				/>
			</Grid>
		</>
	);
};
